// Loan dashboard — twelve-month payment timeline from the v3 servicing
// ledger: one cell per month (on-time = success, late = warning,
// partial/missed/NSF = error, nothing due = muted) with a small
// principal-balance sparkline beneath. Click → Payments tab. Shorter
// history renders the available months only — never fabricated; months
// served by the frozen pre-v3 tape are labelled "legacy" in the tooltip.

import { Link } from "react-router-dom";
import { Card, Skeleton } from "@/design-system/components/ui";
import type { ServicingLedgerPeriodDto } from "@/api/portfolioAdmin/servicingApi";
import {
  formatCalendarDate,
  hasCalendarDayArrived,
  isoDateInTz,
} from "@/design-system/lib/format";
import { useUserTimeZone } from "@/utils/datetime";

const MONTHS = 12;

type CellTone = "success" | "warning" | "error" | "muted";

const CELL_CLASS: Record<CellTone, string> = {
  success: "bg-status-success",
  warning: "bg-status-warning",
  error: "bg-status-error",
  muted: "bg-white-warm border border-border-light",
};

/** Period status → cell tone. Same vocabulary as the ledger table:
 *  on-time/prepaid green, late amber, partial/missed/NSF red; upcoming or
 *  no period = muted "no payment due". */
export function cellToneFor(status: string | null): CellTone {
  switch (status) {
    case "on-time":
    case "prepaid":
      return "success";
    case "late":
      return "warning";
    case "partial":
    case "missed":
    case "nsf":
      return "error";
    default:
      return "muted";
  }
}

interface PaymentTimelineCardProps {
  loanId: string;
  periods: ServicingLedgerPeriodDto[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

interface MonthCell {
  key: string;
  label: string;
  tone: CellTone;
  source: string | null;
  title: string;
  balance: number | null;
}

export function buildMonthCells(
  periods: ServicingLedgerPeriodDto[],
  tz?: string | null,
  now = new Date(),
): MonthCell[] {
  // Periods due in the trailing 12 months ending this month. No period in
  // a month = "no payment due" (muted) — never fabricated history; an
  // empty schedule renders the empty state, not 12 blank cells.
  if (periods.length === 0) return [];
  const byMonth = new Map<string, ServicingLedgerPeriodDto>();
  for (const p of periods) {
    const d = new Date(p.dueDate);
    byMonth.set(`${d.getUTCFullYear()}-${d.getUTCMonth()}`, p);
  }

  // Only render from the first scheduled period forward — a loan younger
  // than 12 months shows its real history, not padded empty months.
  const first = periods.length > 0 ? new Date(periods[0].dueDate) : null;

  // The rightmost bucket is the month the VIEWER is standing in, not UTC's.
  // `now` is a real instant, so "which month is it" is a question about the
  // viewer's zone — whereas a `dueDate` above is a wall-calendar day and stays
  // in UTC. The two sides are read in different zones deliberately, exactly as
  // `calendarDaysUntil` documents. Reading `now` in UTC rolled the whole
  // timeline forward a month from 17:00 Phoenix on the last day of the month.
  const viewerToday = isoDateInTz(now, tz); // "YYYY-MM-DD" in the viewer's zone
  const viewerYear = Number(viewerToday.slice(0, 4));
  const viewerMonthIndex = Number(viewerToday.slice(5, 7)) - 1;

  const cells: MonthCell[] = [];
  for (let i = MONTHS - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(viewerYear, viewerMonthIndex - i, 1));
    if (first && d < new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1))) {
      continue;
    }
    const period = byMonth.get(`${d.getUTCFullYear()}-${d.getUTCMonth()}`) ?? null;
    const monthLabel = d.toLocaleDateString("en-US", {
      month: "short",
      timeZone: "UTC",
    });
    // A period's status is only shown once its DUE DAY has arrived in the
    // viewer's zone. The instant comparison this replaces read a UTC-midnight
    // wall-calendar `dueDate` against the clock, so from 17:00 Phoenix a
    // period due TOMORROW already painted its real status — seven hours before
    // the day it is due even begins for the viewer. Inclusive on the due day
    // itself (`hasCalendarDayArrived`), which also repairs the other boundary:
    // a noon-UTC-stored dueDate used to stay muted until 05:00 local on the
    // very day it was due.
    const tone =
      period && hasCalendarDayArrived(period.dueDate, now)
        ? cellToneFor(period.status)
        : "muted";
    cells.push({
      key: `${d.getUTCFullYear()}-${d.getUTCMonth()}`,
      label: monthLabel,
      tone,
      source: period?.source ?? null,
      title: period
        ? `${formatCalendarDate(period.dueDate)} — ${period.status}` +
          (period.source === "legacy" ? " (legacy)" : "")
        : `${monthLabel} — no payment due`,
      balance: period?.principalBalanceAfter ?? null,
    });
  }
  return cells;
}

export function PaymentTimelineCard({
  loanId,
  periods,
  isLoading,
  isError,
}: PaymentTimelineCardProps) {
  const tz = useUserTimeZone();
  const cells = buildMonthCells(periods ?? [], tz);
  const balances = cells
    .map((c, i) => ({ i, balance: c.balance }))
    .filter((p): p is { i: number; balance: number } => p.balance !== null);

  return (
    <Card data-testid="payment-timeline-card">
      <div className="flex items-baseline justify-between mb-space-2">
        <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0">
          Payment timeline
        </h3>
        <Link
          to={`/portfolio-admin/loans/${loanId}?tab=payment-tracking`}
          className="text-caption text-gold-shadow hover:underline"
        >
          Open Payments →
        </Link>
      </div>

      {isLoading && <Skeleton className="h-14" />}

      {isError && !isLoading && (
        <p className="text-small text-status-error m-0">
          Could not load the payment ledger. Refresh to retry.
        </p>
      )}

      {!isLoading && !isError && cells.length === 0 && (
        <p className="text-small text-gray-mid m-0" data-testid="payment-timeline-empty">
          No payment schedule yet.
        </p>
      )}

      {!isLoading && !isError && cells.length > 0 && (
        <Link
          to={`/portfolio-admin/loans/${loanId}?tab=payment-tracking`}
          className="block no-underline"
          aria-label="Open the Payment Tracking tab"
        >
          <div
            className="grid gap-space-1"
            style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}
            data-testid="payment-timeline-cells"
          >
            {cells.map((c) => (
              <div key={c.key} className="flex flex-col items-center gap-[2px]">
                <span
                  className={`h-4 w-full rounded-sm ${CELL_CLASS[c.tone]}`}
                  title={c.title}
                  data-testid={`timeline-cell-${c.key}-${c.tone}`}
                />
                <span className="text-caption text-gray-mid">{c.label}</span>
              </div>
            ))}
          </div>
          {balances.length >= 2 && (
            <BalanceSparkline
              points={balances}
              cellCount={cells.length}
            />
          )}
        </Link>
      )}
    </Card>
  );
}

/** Tiny inline principal-balance sparkline (svg polyline; stroke follows
 *  the muted text token via currentColor — no hardcoded colors). */
function BalanceSparkline({
  points,
  cellCount,
}: {
  points: { i: number; balance: number }[];
  cellCount: number;
}) {
  const width = 240;
  const height = 28;
  const max = Math.max(...points.map((p) => p.balance));
  const min = Math.min(...points.map((p) => p.balance));
  const span = max - min || 1;
  const x = (i: number) => (cellCount === 1 ? 0 : (i / (cellCount - 1)) * width);
  const y = (b: number) => height - 4 - ((b - min) / span) * (height - 8);
  const path = points.map((p) => `${x(p.i).toFixed(1)},${y(p.balance).toFixed(1)}`).join(" ");

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="w-full h-7 mt-space-1 text-gray-mid"
      role="img"
      aria-label="Principal balance trend"
      data-testid="payment-timeline-sparkline"
    >
      <polyline
        points={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
